import { z } from "zod";

import { MemorisationStateSchema } from "./memorisation";
import { CollectionSchema, PersonSchema, TagSchema } from "./organization";
import { PersonalDuaSchema } from "./personal-dua";
import { ReflectionSchema } from "./reflection";
import { SessionBlockSchema, SessionSchema } from "./session";
import {
  AppMetadataSchema,
  AppSettingsSchema,
  BackupMetadataSchema,
} from "./settings";
import { IsoTimestampSchema, normalizeLabel } from "./shared";

const MAX_BACKUP_RECORDS_PER_TABLE = 100_000;

const WorkspaceDataObjectSchema = z
  .object({
    personalDuas: z.array(PersonalDuaSchema).max(MAX_BACKUP_RECORDS_PER_TABLE),
    collections: z.array(CollectionSchema).max(MAX_BACKUP_RECORDS_PER_TABLE),
    tags: z.array(TagSchema).max(MAX_BACKUP_RECORDS_PER_TABLE),
    people: z.array(PersonSchema).max(MAX_BACKUP_RECORDS_PER_TABLE),
    reflections: z.array(ReflectionSchema).max(MAX_BACKUP_RECORDS_PER_TABLE),
    sessions: z.array(SessionSchema).max(MAX_BACKUP_RECORDS_PER_TABLE),
    sessionBlocks: z.array(SessionBlockSchema).max(MAX_BACKUP_RECORDS_PER_TABLE),
    memorisationStates: z.array(MemorisationStateSchema).max(MAX_BACKUP_RECORDS_PER_TABLE),
    settings: AppSettingsSchema,
    appMetadata: AppMetadataSchema,
    backupMetadata: BackupMetadataSchema,
  })
  .strict();

type WorkspaceDataCandidate = z.output<typeof WorkspaceDataObjectSchema>;
type WorkspaceArrayKey = {
  [Key in keyof WorkspaceDataCandidate]: WorkspaceDataCandidate[Key] extends readonly { id: string }[]
    ? Key
    : never;
}[keyof WorkspaceDataCandidate];

const WORKSPACE_ARRAY_KEYS = [
  "personalDuas",
  "collections",
  "tags",
  "people",
  "reflections",
  "sessions",
  "sessionBlocks",
  "memorisationStates",
] as const satisfies readonly WorkspaceArrayKey[];

function addMissingReferenceIssue(
  context: z.RefinementCtx,
  path: (string | number)[],
  entityName: string,
  id: string,
): void {
  context.addIssue({
    code: "custom",
    message: `${entityName} reference ${id} does not exist in this workspace.`,
    path,
  });
}

function validateUniqueActiveNormalizedNames(
  records: readonly { normalizedName: string; deletedAt?: string | undefined }[],
  table: "collections" | "tags",
  context: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  records.forEach((record, index) => {
    if (record.deletedAt !== undefined) return;
    if (seen.has(record.normalizedName)) {
      context.addIssue({
        code: "custom",
        message: `Duplicate active ${table} normalizedName: ${record.normalizedName}`,
        path: [table, index, "normalizedName"],
      });
    }
    seen.add(record.normalizedName);
  });
}

function validateWorkspaceIntegrity(data: WorkspaceDataCandidate, context: z.RefinementCtx): void {
  for (const key of WORKSPACE_ARRAY_KEYS) {
    const ids = new Set<string>();
    data[key].forEach((record, index) => {
      if (ids.has(record.id)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate ${key} ID: ${record.id}`,
          path: [key, index, "id"],
        });
      }
      ids.add(record.id);
    });
  }

  const collectionIds = new Set(data.collections.map(({ id }) => id));
  const tagIds = new Set(data.tags.map(({ id }) => id));
  const personIds = new Set(data.people.map(({ id }) => id));
  const duaIds = new Set(data.personalDuas.map(({ id }) => id));
  const sessionIds = new Set(data.sessions.map(({ id }) => id));

  validateUniqueActiveNormalizedNames(data.collections, "collections", context);
  validateUniqueActiveNormalizedNames(data.tags, "tags", context);

  data.collections.forEach((collection, index) => {
    if (collection.normalizedName !== normalizeLabel(collection.name)) {
      context.addIssue({
        code: "custom",
        message: "Collection normalizedName does not match its name.",
        path: ["collections", index, "normalizedName"],
      });
    }
  });
  data.tags.forEach((tag, index) => {
    if (tag.normalizedName !== normalizeLabel(tag.name)) {
      context.addIssue({
        code: "custom",
        message: "Tag normalizedName does not match its name.",
        path: ["tags", index, "normalizedName"],
      });
    }
  });
  data.people.forEach((person, index) => {
    if (person.normalizedName !== normalizeLabel(person.displayName)) {
      context.addIssue({
        code: "custom",
        message: "Person normalizedName does not match its displayName.",
        path: ["people", index, "normalizedName"],
      });
    }
  });

  data.settings.focus.collectionIds.forEach((collectionId, index) => {
    if (!collectionIds.has(collectionId)) {
      addMissingReferenceIssue(
        context,
        ["settings", "focus", "collectionIds", index],
        "Collection",
        collectionId,
      );
    }
  });
  data.settings.focus.personIds.forEach((personId, index) => {
    if (!personIds.has(personId)) {
      addMissingReferenceIssue(
        context,
        ["settings", "focus", "personIds", index],
        "Person",
        personId,
      );
    }
  });

  data.personalDuas.forEach((dua, duaIndex) => {
    const relationshipSets = [
      ["collectionIds", dua.collectionIds, collectionIds, "Collection"],
      ["tagIds", dua.tagIds, tagIds, "Tag"],
      ["personIds", dua.personIds, personIds, "Person"],
      ["sessionIds", dua.sessionIds, sessionIds, "Session"],
    ] as const;
    for (const [field, references, availableIds, entityName] of relationshipSets) {
      references.forEach((id, referenceIndex) => {
        if (!availableIds.has(id)) {
          addMissingReferenceIssue(context, ["personalDuas", duaIndex, field, referenceIndex], entityName, id);
        }
      });
    }
  });

  data.reflections.forEach((reflection, reflectionIndex) => {
    if (!duaIds.has(reflection.duaId)) {
      addMissingReferenceIssue(
        context,
        ["reflections", reflectionIndex, "duaId"],
        "PersonalDua",
        reflection.duaId,
      );
    }
    reflection.personIds.forEach((personId, personIndex) => {
      if (!personIds.has(personId)) {
        addMissingReferenceIssue(
          context,
          ["reflections", reflectionIndex, "personIds", personIndex],
          "Person",
          personId,
        );
      }
    });
  });

  data.sessionBlocks.forEach((block, blockIndex) => {
    if (!sessionIds.has(block.sessionId)) {
      addMissingReferenceIssue(
        context,
        ["sessionBlocks", blockIndex, "sessionId"],
        "Session",
        block.sessionId,
      );
    }
    if (block.sourceId === undefined) return;
    const sourceIds =
      block.type === "collection"
        ? collectionIds
        : block.type === "tag"
          ? tagIds
          : block.type === "person"
            ? personIds
            : block.type === "dua"
              ? duaIds
              : undefined;
    if (sourceIds !== undefined && !sourceIds.has(block.sourceId)) {
      addMissingReferenceIssue(
        context,
        ["sessionBlocks", blockIndex, "sourceId"],
        block.type,
        block.sourceId,
      );
    }
  });

  const memorisationTargets = new Set<string>();
  data.memorisationStates.forEach((state, stateIndex) => {
    const targetKey = `${state.targetType}:${state.targetId}`;
    if (memorisationTargets.has(targetKey)) {
      context.addIssue({
        code: "custom",
        message: `Duplicate memorisation target: ${targetKey}`,
        path: ["memorisationStates", stateIndex, "targetId"],
      });
    }
    memorisationTargets.add(targetKey);
    if (state.targetType === "personal_dua" && !duaIds.has(state.targetId)) {
      addMissingReferenceIssue(
        context,
        ["memorisationStates", stateIndex, "targetId"],
        "PersonalDua",
        state.targetId,
      );
    }
  });
}

export const WorkspaceDataSchema = WorkspaceDataObjectSchema.superRefine(validateWorkspaceIntegrity);

export const WorkspaceBackupPayloadSchema = z
  .object({
    schemaVersion: z.literal(1),
    exportedAt: IsoTimestampSchema,
    data: WorkspaceDataSchema,
  })
  .strict();

const BackupHeaderShape = {
  format: z.literal("dua-workspace-backup"),
  version: z.literal(1),
  createdAt: IsoTimestampSchema,
  appVersion: z.string().trim().min(1).max(80),
} satisfies z.ZodRawShape;

export const BackupEncryptionMetadataSchema = z
  .object({
    kdf: z.literal("PBKDF2-SHA-256"),
    iterations: z.number().int().min(100_000).max(2_000_000),
    salt: z.string().min(16).max(1_024),
    cipher: z.literal("AES-256-GCM"),
    iv: z.string().min(12).max(1_024),
  })
  .strict();

export const UnencryptedBackupEnvelopeSchema = z
  .object({
    ...BackupHeaderShape,
    encrypted: z.literal(false),
    payload: WorkspaceBackupPayloadSchema,
  })
  .strict();

export const EncryptedBackupEnvelopeSchema = z
  .object({
    ...BackupHeaderShape,
    encrypted: z.literal(true),
    encryption: BackupEncryptionMetadataSchema,
    payload: z.string().min(1).max(50 * 1024 * 1024),
  })
  .strict();

export const BackupEnvelopeSchema = z.discriminatedUnion("encrypted", [
  UnencryptedBackupEnvelopeSchema,
  EncryptedBackupEnvelopeSchema,
]);

export type WorkspaceData = z.output<typeof WorkspaceDataSchema>;
export type WorkspaceBackupPayload = z.output<typeof WorkspaceBackupPayloadSchema>;
export type BackupEnvelope = z.output<typeof BackupEnvelopeSchema>;
export type BackupEncryptionMetadata = z.output<typeof BackupEncryptionMetadataSchema>;
